/**
 * Runtime polyfills for the handful of newer platform APIs the game (or a
 * library it loads) uses without a fallback. Imported first from main.js so
 * they are installed before any other module's top-level code runs.
 *
 * Every entry here is standards-shaped and installed only when missing, so
 * modern browsers never see them. Each one exists because a real player hit
 * the gap in production:
 *
 *   - CanvasRenderingContext2D.roundRect — the Canvas2D HUD draws every bar,
 *     button and panel with it; a browser without it (Baidu Explorer on
 *     Android 10, some older WebViews) threw on the first HUD frame and the
 *     game never drew (Sentry LORD-OF-THE-SWARM-J).
 *   - Array.prototype.findLast / findLastIndex — PostHog's lazily loaded
 *     web-vitals chunk uses them; Chrome < 97 and Safari < 15.4 lack them
 *     (LORD-OF-THE-SWARM-H).
 *
 * Note this runs in the page only. The worker (nn_inference_worker.js) has
 * its own entry point and needs none of these.
 */

/**
 * Spec-shaped roundRect: `radii` may be a number, or an array of 1–4 values
 * in the same order as CSS border-radius (all | [tl+br, tr+bl] | [tl, tr+bl,
 * br] | [tl, tr, br, bl]). DOMPointInit radii (elliptical corners) are
 * accepted but flattened to their x radius — nothing in the game uses them.
 * @this {CanvasRenderingContext2D|Path2D}
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 * @param {number|Array<number|{x: number, y?: number}>} [radii]
 * @returns {void}
 */
export function roundRectPolyfill(x, y, w, h, radii = 0) {
    const toNum = (r) => (typeof r === 'object' && r !== null ? Number(r.x) || 0 : Number(r) || 0);
    let tl, tr, br, bl;
    if (Array.isArray(radii)) {
        const r = radii.map(toNum);
        if (r.length === 0) tl = tr = br = bl = 0;
        else if (r.length === 1) tl = tr = br = bl = r[0];
        else if (r.length === 2) { tl = br = r[0]; tr = bl = r[1]; }
        else if (r.length === 3) { tl = r[0]; tr = bl = r[1]; br = r[2]; }
        else { [tl, tr, br, bl] = r; }
    } else {
        tl = tr = br = bl = toNum(radii);
    }
    // Clamp like the spec: no corner may exceed half the box on either axis,
    // and a negative box flips the direction the path is traced in.
    const maxR = Math.min(Math.abs(w), Math.abs(h)) / 2;
    tl = Math.max(0, Math.min(tl, maxR));
    tr = Math.max(0, Math.min(tr, maxR));
    br = Math.max(0, Math.min(br, maxR));
    bl = Math.max(0, Math.min(bl, maxR));

    this.moveTo(x + tl, y);
    this.lineTo(x + w - tr, y);
    if (tr) this.arcTo(x + w, y, x + w, y + tr, tr);
    this.lineTo(x + w, y + h - br);
    if (br) this.arcTo(x + w, y + h, x + w - br, y + h, br);
    this.lineTo(x + bl, y + h);
    if (bl) this.arcTo(x, y + h, x, y + h - bl, bl);
    this.lineTo(x, y + tl);
    if (tl) this.arcTo(x, y, x + tl, y, tl);
    this.closePath();
}

/**
 * @this {unknown[]}
 * @param {(value: unknown, index: number, array: unknown[]) => boolean} predicate
 * @param {unknown} [thisArg]
 * @returns {number}
 */
function findLastIndexPolyfill(predicate, thisArg) {
    if (typeof predicate !== 'function') {
        throw new TypeError(`${predicate} is not a function`);
    }
    for (let i = this.length - 1; i >= 0; i -= 1) {
        if (predicate.call(thisArg, this[i], i, this)) return i;
    }
    return -1;
}

/**
 * @this {unknown[]}
 * @param {(value: unknown, index: number, array: unknown[]) => boolean} predicate
 * @param {unknown} [thisArg]
 * @returns {unknown}
 */
function findLastPolyfill(predicate, thisArg) {
    const i = findLastIndexPolyfill.call(this, predicate, thisArg);
    return i === -1 ? undefined : this[i];
}

/**
 * Install whatever is missing on the given global. Idempotent; safe to call
 * in Node/jsdom where the canvas classes may not exist at all.
 * @param {object} [g] the global object to patch (defaults to globalThis)
 * @returns {void}
 */
export function installPolyfills(g = globalThis) {
    for (const name of ['CanvasRenderingContext2D', 'OffscreenCanvasRenderingContext2D', 'Path2D']) {
        const proto = g[name]?.prototype;
        if (proto && typeof proto.roundRect !== 'function') {
            Object.defineProperty(proto, 'roundRect', {
                value: roundRectPolyfill, writable: true, configurable: true, enumerable: false,
            });
        }
    }
    const arrayProto = g.Array?.prototype;
    if (arrayProto) {
        if (typeof arrayProto.findLast !== 'function') {
            Object.defineProperty(arrayProto, 'findLast', {
                value: findLastPolyfill, writable: true, configurable: true, enumerable: false,
            });
        }
        if (typeof arrayProto.findLastIndex !== 'function') {
            Object.defineProperty(arrayProto, 'findLastIndex', {
                value: findLastIndexPolyfill, writable: true, configurable: true, enumerable: false,
            });
        }
    }
}

installPolyfills();
